import React, { useState, useEffect, useRef } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { SharedActionCreators, sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { Button, ButtonGroup } from 'components/ui/button'
import { CheckboxInputElement } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { FieldSection } from 'components/ui/fieldset'
import { IconSuccess, IconWarning, IconPhone, IconPhoneLock, IconChanges } from 'components/ui/icons'
import { Description, listItemBaseStyles } from 'components/ui/list-item-label'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { PublishDetails } from './sidebar'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { ValidationFailuresList } from 'domains/org/getty/components/publishing/validationsList'
import { ErrorsList } from 'domains/org/getty/components/publishing/errorsList'
import { GettyStore, GettyAppState } from 'domains/org/getty/store'

import { Page } from "generated/mos/pagination"
import { BundleSummary, Release } from 'generated/mos/org/getty/publish'
import { ListBundlesRequest, ListReleasesRequest } from 'generated/mos/org/getty/publishaccess'
import { CreateReleaseRequest } from 'generated/mos/org/getty/publishmanagement'
import { BundleType } from "generated/mos/releasecontrols"

import { pick, isNotNullOrUndefined } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { timestampToFormattedString } from 'helpers/timestamps'


import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import styled from 'styled'

const Title = styled.h2`
  margin-top: 0;
  margin-bottom: ${space(4)};
  color: ${({ theme }) => theme.color.grayD80};
  font-size: ${({ theme }) => theme.font.size.base};
  font-weight: ${({ theme }) => theme.font.weight.base};
  line-height: 1.6;
`

const Details = styled(Description)`
  width: 100%;
`

const BundleIconStyles = `
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  display: flex;
  border-radius: 4px;
  margin-right: ${space(4)}
`

const PrivateBundleIcon = styled.span`
  ${BundleIconStyles}
  background: #334563;
`

const PublicBundleIcon = styled.span`
  ${BundleIconStyles}
  background: #1D94D0;
`

const BundleItem = styled.label<{ disabled?: boolean }>`
  ${listItemBaseStyles}
  align-items: flex-start;
  height: auto;
  background: ${({ theme }) => theme.color.white };
`

const EntityCountItem = styled.li`
  padding-bottom: ${space(2)};
  color: ${({ theme }) => theme.color.gray };
  & > span {
    display: inline-block;
    width: 120px;
  }
`

const BundleHeader = styled.div`
  display: flex;
  flex-diection: row;
  align-items: center;
  padding-bottom: ${space(4)};
  margin-bottom: ${space(4)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL70 };
`

const BundleTitle = styled.strong`
  color: ${({ theme }) => theme.color.grayD80};
  font-size: ${({ theme }) => theme.font.size.base};
  font-weight: ${({ theme }) => theme.font.weight.base};
  line-height: 1.6;
`

const BundleSubtitle = styled.span`
  font-size: ${({ theme }) => theme.font.size.xSmall };
  color: ${({ theme }) => theme.color.gray };
  display: block;
`

const BundleDetails = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: ${space(4)};
  margin-bottom: ${space(4)};
  border-left: 3px solid ${({ theme }) => theme.color.grayL70};
`

const BundleNotification = styled.div`
  display: flex;
  flex-diection: row;
  align-items: center;
  padding-top: ${space(4)};
  margin-bottom: ${space(4)};
  border-top: 1px solid ${({ theme }) => theme.color.grayL70 };
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.grayD80};
  & > *:first-child {
    margin-right: ${space(2)};
  }
`

const PublishStatus = styled.div`
  margin-bottom: ${space(4)};
`

const BUNDLE_TITLES = {
  BUNDLE_TYPE_PRIVATE: {
    title: 'Private Bundle',
    subtitle: 'Private content can be viewed by staff only via the beta app',
    icon: <PrivateBundleIcon color={'primaryD80'}><IconPhoneLock color={'white'} /></PrivateBundleIcon>
  },
  BUNDLE_TYPE_PUBLIC: {
    title: 'Public Bundle',
    subtitle: 'Public content is made visible to the public',
    icon: <PublicBundleIcon><IconPhone color={'white'} /></PublicBundleIcon>
  },
}

const POLL_FREQUENCY = 10000

type FormValues = {
  releaseNotes: string;
} & Partial<{
  [key in BundleType]: boolean;
}>

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetListBundleSummariesRequest' | 'gettyCreateReleaseRequest' | 'gettyGetListBundlesRequest' | 'gettyGetListReleasesRequest' | 'gettyCreateReleaseReset'> &
  Pick<SharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyAppState, 'publishBundleSummaries' | 'publishReleaseCreate' | 'publishBundlesList' | 'publishReleases'>

type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps;

const publishDate = (releases: ReadonlyArray<Release.Entity> | undefined) => {
  if (releases) {
    const latestCompletedPublicRelease = releases.find(({bundles}) => {
      return bundles.some(({bundleType, processingStatus}) => {
        return bundleType === 'BUNDLE_TYPE_PUBLIC' && processingStatus === 'BUNDLE_PROCESSING_COMPLETED'
      })
    })
    if (latestCompletedPublicRelease && latestCompletedPublicRelease.metadata && latestCompletedPublicRelease.metadata.modifiedAt) {
      return timestampToFormattedString(latestCompletedPublicRelease.metadata.modifiedAt.value);
    }
  }
  return '-';
}

const useInterval = (callback: () => void, delay: number) => {
  const savedCallback = useRef<() => void>(() => {});
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay]);
}

const PublishingPageView = (props: Props) => {
  const {
    projectId,
    gettyGetListBundleSummariesRequest,
    toastNotification,
    publishBundlesList,
    publishReleases,
    publishReleaseCreate,
    gettyGetListBundlesRequest,
    gettyCreateReleaseRequest,
    gettyGetListReleasesRequest,
    publishBundleSummaries,
    gettyCreateReleaseReset,
  } = props

  const data = statusSelector.data(publishBundleSummaries)
  const publishReleasesData = statusSelector.data(publishReleases)
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ isPublishing, setIsPublishing ] = useState<boolean>(false)
  const hasSubmitted = useRef<boolean>(false)

  useEffect(() => {
    gettyGetListReleasesRequest({
      ...ListReleasesRequest.defaults,
      sortOrder: 'SORT_CREATED_DESC',
      page: {
        ...Page.defaults,
        pageSize: 20,
      }
    })
    gettyGetListBundleSummariesRequest()
    gettyGetListBundlesRequest({
      ...ListBundlesRequest.defaults,
      filter: {
        type: ListBundlesRequest.ByProcessingStatus.refName,
        processingStatuses: ["BUNDLE_PROCESSING_RUNNING"]
      }
    })
    hasSubmitted.current = false

  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // poll bundle status.
  useInterval(() => {
    gettyGetListBundlesRequest({
      ...ListBundlesRequest.defaults,
      filter: {
        type: ListBundlesRequest.ByProcessingStatus.refName,
        processingStatuses: ["BUNDLE_PROCESSING_RUNNING"]
      }
    })
  }, POLL_FREQUENCY);

  useEffect(() => {
    if(publishBundleSummaries.status === Status.Ready) {
      const bundles = data && data.reduce((acc, curr) => ({ ...acc, [curr.bundleType]: false }), {})
      setFormBag(createFormBag({
        releaseNotes: '',
        ...bundles,
      }))
      hasSubmitted.current = false
    }
  }, [publishBundleSummaries.status, data])

  useEffect(() => {
    if(publishBundlesList.status === Status.Ready && publishBundlesList.data.length > 0 || publishBundlesList.status === Status.Updating && publishBundlesList.data.length > 0) {
      setIsPublishing(true)
    } else setIsPublishing(false)
  },[publishBundlesList])

  useEffect(() => {
    if(publishReleaseCreate.status === Status.Ready) {
      const { status } = publishReleaseCreate.data
      status === 'OK' && toastNotification({
        type: 'success',
        text: 'A new release is now being published',
      })
      status === 'ERROR_PUBLISH_ALREADY_IN_PROGRESS' &&  toastNotification({
        type: 'info',
        text: 'A new release is currently being published',
      })
      status === 'ERROR_PUBLIC_BUNDLE_HAS_OUTSTANDING_ISSUES' && toastNotification({
        type: 'error',
        text: 'Validation issues need to be resolved before new public bundles can be published',
      })
      gettyCreateReleaseReset()
    }
    hasSubmitted.current = false
  }, [publishReleaseCreate, toastNotification]) // eslint-disable-line react-hooks/exhaustive-deps


  const submit = () => {
    if(formBag) {
      const { releaseNotes, ...rest } = formBag.values
      const bundleTypesToPublish: ReadonlyArray<BundleType> = Object.entries(rest).map(([key, value]) => value ? key as BundleType : null).filter(isNotNullOrUndefined)
      if(bundleTypesToPublish.length) {
        hasSubmitted.current = true
        gettyCreateReleaseRequest({
          ...CreateReleaseRequest.defaults,
          bundleTypesToPublish,
          releaseNotes,
        })
      }
    }
  }

  const title = 'Publish "GettyGuide"'

  return (
    <LayoutListOrFormPage
      nav={() => (
        <PrimaryNavigationEditEntity
          title="Publish Project"
          navBackRoute={`/project/${projectId}`}
          darkTheme
        />
      )}
      title={title}
      subnav={() => (
        <>
          <ErrorsList projectId={projectId} />
          <SecondaryNavigation
            navItems={[]}
        />
        </>
      )}
      toolbar={() => {
        return <ValidationFailuresList projectId={projectId} />
      }}
      content={() => (
        <StatusGuard status={publishBundleSummaries}>
          <Title>What would you like to publish?</Title>
          {isPublishing && <PublishStatus>
            <StatusSummary info={[{ text: 'Publish currently in progress' }]} />
            </PublishStatus>}
          {formBag && data && <form noValidate onSubmit={(event) => { event.preventDefault() }}>
            <FormData
              bag={formBag}
              onUpdate={(event) => {
                setFormBag(event.bag)
              }}
              validate={() => ({})}
            >
              {data
                .filter(({ bundleType }: BundleSummary.Entity) => bundleType !== 'BUNDLE_TYPE_UNSPECIFIED')
                .map(({ bundleType, entityCounts, unpublishedChangeCount, issueCount }: BundleSummary.Entity) => {
                  return bundleType !== 'BUNDLE_TYPE_UNSPECIFIED' ? (
                    <BundleItem key={bundleType} disabled={isPublishing}>
                      <CheckboxInputElement
                        checked={formBag.values[bundleType]}
                        disabled={isPublishing}
                        onClick={() => setFormBag({
                          ...formBag,
                          values: {
                            ...formBag.values,
                            [bundleType]: !formBag.values[bundleType]
                          }
                        })}
                        />
                      <Details>
                        <BundleHeader>
                          {BUNDLE_TITLES[bundleType].icon}
                          <div>
                            <BundleTitle>{BUNDLE_TITLES[bundleType].title}</BundleTitle>
                            <BundleSubtitle>{BUNDLE_TITLES[bundleType].subtitle}</BundleSubtitle>
                          </div>
                        </BundleHeader>
                        <BundleDetails>
                          {entityCounts.map(({ entityDisplayName, entityTypename, count }: BundleSummary.EntityCount.Entity) => (
                            <EntityCountItem key={entityTypename}><span>{entityDisplayName}</span>{count}</EntityCountItem>
                          ))}
                        </BundleDetails>
                        <BundleNotification>
                          <IconChanges color="primary" />
                          <span>{unpublishedChangeCount} Unpublished Changes</span>
                        </BundleNotification>
                        <BundleNotification>
                          <IconWarning color="warning" />
                          <span>{issueCount} Issues</span>
                        </BundleNotification>
                      </Details>
                    </BundleItem>
                  ) : null
              })}
              <FieldSection />
              <Input
                field="releaseNotes"
                label="Release notes"
                labelSupportingText="(internal notes)"
                component="textarea"
                isDisabled={isPublishing}
              />
            </FormData>
            <ButtonGroup>
              <Button appearance="positive" isDisabled={isPublishing || hasSubmitted.current} onClick={submit}>Publish</Button>
            </ButtonGroup>
          </form>
        }
        </StatusGuard>
      )}
      sidebar={() => (
        <PublishDetails publishDate={publishDate(publishReleasesData)} projectId={projectId} />
      )}
    />
  )
}

export const PublishingPage = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => pick(store.getty, 'publishBundleSummaries', 'publishReleaseCreate', 'publishBundlesList', 'publishReleases'),
  {
    ...pick(actionCreators, 'gettyGetListBundleSummariesRequest', 'gettyCreateReleaseRequest', 'gettyGetListBundlesRequest', 'gettyGetListReleasesRequest', 'gettyCreateReleaseReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(PublishingPageView)
